/**
 * Item schemas — nouns, visibility, portability, and use-interactions (plan §3).
 *
 * @remarks
 * Items feed two consumers: catalog derivation (`engine-runtime/entities`
 * builds each turn's `ParseCandidateEntity[]` from `nouns` / `aliases` /
 * `adjectives`) and the turn pipeline (use-interactions supply the v0
 * condition→effect grammar for `use`/`unlock`). Descriptions and interaction
 * narratives are the item half of narrative v0: plain response strings,
 * rendered by the engine as structured transcript entries.
 *
 * @packageDocumentation
 */

import { z } from 'zod';

import { armorSchema, weaponSchema } from './combat.js';
import { conditionalTextSchema, descriptionFragmentSchema } from './conditional-text.js';
import { conditionSchema } from './conditions.js';
import { containedInSchema } from './containment.js';
import { itemStockSchema } from './counters.js';
import { groupIdSchema, itemIdSchema } from './identifiers.js';
import { durabilitySchema, equippableSchema, refusedActsOf } from './item-wear.js';
import { itemLogSchema } from './logs.js';
import { nonBlankStringSchema, positiveIntegerSchema } from './primitives.js';
import { spokenWhenOptionalSchema } from './spoken-when.js';
import { topicsSchema } from './topics.js';
import { narrativeLineSchema } from './transcript.js';
import { useInteractionSchema } from './use-interactions.js';
import { canonicalVerbKeySchema, verbInteractionsSchema } from './verb-interactions.js';

// The `use` interaction left for its own module at the 500-line cap (D234); both names still resolve from here.
export { isUsedAlone, type UseInteraction, useInteractionSchema } from './use-interactions.js';
// Containment left at the cap too (D240), when a body became a container; both names still resolve from here.
export { type ContainedIn, containedInSchema } from './containment.js';

/**
 * D47: per-item lines for a recognized-but-unmechanized verb, keyed by
 * canonical verb token.
 *
 * @remarks
 * Shaped after `askNarratives` in every respect — optional, `min(1)` when
 * present, ordinary narrative lines — because that precedent has been stable
 * since R1.1 and authors already know it.
 */
export const unsupportedVerbNarrativesSchema = z
	.record(canonicalVerbKeySchema, z.array(narrativeLineSchema).min(1).readonly())
	.readonly();

/** A pack's per-item verb answers — see {@link unsupportedVerbNarrativesSchema}. */
export type UnsupportedVerbNarratives = z.infer<typeof unsupportedVerbNarrativesSchema>;

/**
 * D50: a thing with a DOOR — open or closed, and able to say so.
 *
 * @remarks
 * Deliberately NOT a sub-field of containment, which was the first draft and
 * forced an abuse Calder spotted while authoring (§4.3): a floor grille holds
 * nothing, but `close the grille` is a real instinct once it has been pried,
 * and expressing that meant declaring it a container of nothing.
 *
 * Openable and containing are two properties. A locker has both. A grille has
 * only a door. A collapsed shelf has only contents. Neither implies the other,
 * which is why "container" is not a declared kind here at all — a thing simply
 * becomes one when something else points at it.
 *
 * The per-state prose lives INSIDE this field rather than beside it, so
 * "open/closed descriptions on a thing with no door" is unrepresentable rather
 * than merely rejected. Absent either description, the item's base
 * `description` speaks for both states.
 *
 * D55 amendment: that pair is now SUGAR. It desugars to two ordinary
 * `container_open` variants over the item's `description` — one code path, two
 * spellings — so it can never drift from what it is shorthand for, and so a
 * thing with a door AND a switch AND a flag has one composable describer rather
 * than three competing fields. The shorthand stays because it is the common
 * case and it reads well; an author who needs more writes the variants
 * directly, and the schema refuses declaring both (see
 * {@link itemDefinitionSchema}).
 */
export const openableSchema = z
	.strictObject({
		/*
		 * D135: what is said when the door is ALREADY in the position asked for
		 * — D120's rule ("an action that changes nothing must say so, and must
		 * not re-run the narrative of the change") reaching the door verbs,
		 * where it was recorded as still open. Two fields, not one, for D120's
		 * direction-blindness reason; the refusal itself is the handler's, these
		 * only choose the voice over the built-in slots.
		 */
		alreadyClosedNarratives: z.array(narrativeLineSchema).min(1).readonly().optional(),
		alreadyOpenNarratives: z.array(narrativeLineSchema).min(1).readonly().optional(),
		/*
		 * D135 (Calder's §3, the locker-cluster doc): what is said when the door
		 * CLOSES — an EVENT, distinct from the item's description under a
		 * `container_open` variant, which is what the thing looks like once shut. The door verbs used to read the state
		 * description aloud as if it were the event, which reads as explaining
		 * to the player something they just did; a sentence true of both the
		 * movement and the state afterward is a description, not an event.
		 * Absent, the engine's built-in event slot speaks.
		 */
		closedNarratives: z.array(narrativeLineSchema).min(1).readonly().optional(),
		/*
		 * D53: when bare hands are enough. ABSENT means always — most doors are
		 * just doors. Present, and empty hands work only while the condition
		 * holds; otherwise `open` falls through to the instrument path exactly as
		 * it did before doors existed, so a tool puzzle stays a tool puzzle.
		 *
		 * Found by Calder playing past the authored path: D50 gave the wall locker
		 * a door, and "bare-hands open works on anything with a door" turned that
		 * door into a bypass around the game's signature object. The golden
		 * replays could not see it — they drive `use the pry bar on the locker`,
		 * which proves the tool WORKS and never that it is REQUIRED.
		 *
		 * It reuses `conditionSchema` rather than inventing a container lock,
		 * which is both DRY and more expressive than the `ExitLock` mirror the
		 * exit side uses: flags, held items, room, and the combinators all work,
		 * and the evaluator already exists.
		 */
		handOpenCondition: conditionSchema.optional(),
		/*
		 * D253: the door is mesh or glass. What the thing holds is SEEN while it
		 * is shut — named, examined, listed by `search` — and REACHED only once
		 * it opens; until then a hand that would cross the door hears that the
		 * thing is in there and the door is shut. Absent, the door hides what is
		 * behind it, as every door did before (D50). Found by Mike in the
		 * Meridian's stores cage, whose page says what is through the mesh while
		 * `take hand cutter` said there was no hand cutter.
		 */
		isSeeThrough: z.literal(true).optional(),
		/** D135: what is said when the door OPENS — the event twin of `closedNarratives`. */
		openedNarratives: z.array(narrativeLineSchema).min(1).readonly().optional(),
		/** The state this thing STARTS in; runtime state carries it from there. */
		state: z.enum(['closed', 'open']),
	})
	.readonly();

/** A thing with a door — see {@link openableSchema}. */
export type Openable = z.infer<typeof openableSchema>;

/**
 * D135 (Calder's locker-bank doc §4 and item-seven doc §1.1): what is said when
 * the player types `close X` (or `open X`, the twin field) and there is no
 * closing to be had — on the ITEM, beside `unsupportedVerbNarratives`, and
 * deliberately NOT inside `openable`.
 *
 * @remarks
 * Only one of the POC's three one-way things is an `openable` at all; the
 * grille and the security door carry their open state in a flag with a
 * conditional description. Hanging the refusal off `openable` would force
 * both to declare a door purely in order to refuse, handing each an
 * `openable.state` beside the flag that already carries it — two sources of
 * truth for whether a thing is open, which is verbatim the divergence
 * Calder filed as the locker doc's §2 (the text said shut, the picture showed
 * open). So the refusal is a fact about the ITEM: "this does not close."
 *
 * `when` makes it condition-capable, because "you cannot close this" is not
 * true of the grille until the grille is open; absent, the refusal always
 * applies — a bench that will never close. On an `openable`, `when` is
 * REQUIRED (see {@link itemDefinitionSchema}): an unconditional refusal
 * there would make the door one-way by accident and strand its own
 * `close_container` unreachable.
 */
export const doorRefusalSchema = spokenWhenOptionalSchema;

/** One verb refusal in the thing's own words — `open`, `close` or (D199) `take` — see {@link doorRefusalSchema}. */
export type DoorRefusal = z.infer<typeof doorRefusalSchema>;

/**
 * D56: a thing with a SWITCH — on or off, and able to be moved between them.
 *
 * @remarks
 * Carries state and nothing else, and the absence of description fields is the
 * design rather than an omission. `openable` shipped with a per-state pair
 * because D50 had no alternative; D55 gave it one, and repeating that pair here
 * would recreate the exact collision D55 removed — a thing that is
 * open-or-shut AND on-or-off has four readings and one description slot, and
 * two competing per-state fields can only strand three of them. What a switched
 * thing LOOKS like is said by conditional variants on `description`
 * (`switched_on` is an ordinary condition); what happens WHEN it moves is said
 * by the transition narratives below. Those are different questions, and the
 * fiction routinely wants different words for them: "starts to tick, then to
 * roar" is a transition, and "the element is cold" is a state.
 *
 * The property is separate from `openable` for the same reason containment is
 * (D50): a kettle switches and does not open, a locker opens and does not
 * switch, a powered cabinet does both, and none of the three implies another.
 *
 * Declaring this is also what MECHANIZES `turn` for this item. The verb stays
 * declared-unsupported in the vocabulary, so everything that is not a switch
 * keeps earning its authored D47 refusal — which is what makes "if device
 * state ships with exactly one working switch, that is the right number" a
 * thing a pack can actually express.
 */
export const switchableSchema = z
	.strictObject({
		/*
		 * D120: what is said when the switch is ALREADY in the position asked
		 * for. Optional per-item flavor over the built-in slots; the refusal
		 * itself is guaranteed by the handler, so these only choose the voice.
		 *
		 * TWO fields, mirroring `turnOnNarratives`/`turnOffNarratives`, because
		 * one would be direction-blind: a kettle that answers "it is already
		 * going" to a request to switch it OFF is the exact defect this ruling
		 * exists to remove, and a single field makes that the easy mistake.
		 */
		alreadyOffNarratives: z.array(narrativeLineSchema).min(1).readonly().optional(),
		alreadyOnNarratives: z.array(narrativeLineSchema).min(1).readonly().optional(),
		/*
		 * D65: what is said when the switch MOVES and the device does not answer
		 * — the power is out (see `poweredWhen`). Requires `poweredWhen`: a
		 * device with no power condition is never dead, and lines nothing can
		 * reach are the D31 defect.
		 */
		deadNarratives: z.array(narrativeLineSchema).min(1).readonly().optional(),
		/** D65: what is said when a latched switch is asked to come back. Requires `latching`. */
		latchedNarratives: z.array(narrativeLineSchema).min(1).readonly().optional(),
		/*
		 * D65: a switch that can enter a state it cannot LEAVE — forty
		 * dust-welded breaker handles are a thing you can do downhill and cannot
		 * undo. Once thrown AWAY from its authored starting state, reversing is
		 * refused by construction; the prose only flavors a refusal the schema
		 * already guarantees, which is the right relationship between the two
		 * (Calder's ask, engineering_standards §2.1).
		 */
		latching: z.literal(true).optional(),
		/*
		 * D65: WHEN this device has power (Calder's model). Absent, always — a
		 * torch is its own mains. Present and unmet, the switch still MOVES and
		 * the device does not answer: `switched_on` (the condition the describer
		 * reads) means RUNNING — position on AND powered — because "on, and
		 * dead" is the beat the whole model exists to express, and the fiction
		 * is entirely in the gap between the switch and the power.
		 */
		poweredWhen: conditionSchema.optional(),
		/** The state this thing STARTS in; runtime state carries it from there. */
		state: z.enum(['off', 'on']),
		/** What is said when it is switched OFF. Absent, the engine's slot speaks. */
		turnOffNarratives: z.array(narrativeLineSchema).min(1).readonly().optional(),
		/*
		 * D260: a switch held where it is, in the thing's own words — the
		 * breaker under a lockout tag. The take/open/close refusals' shape
		 * (D135, D199): `when` scopes it, absent it always applies; two fields
		 * for D120's reason. Judged after the latch and the unmoved check, and
		 * before the power gate (a held switch does not move, so whether the
		 * device answers never arises). Reach stays `reachableWhen`'s: it bars
		 * every verb, `use … on` included, where this bars the switch alone.
		 */
		turnOffRefusal: doorRefusalSchema.optional(),
		/** What is said when it is switched ON. Absent, the engine's slot speaks. */
		turnOnNarratives: z.array(narrativeLineSchema).min(1).readonly().optional(),
		/** D260: the switch held OFF in its own words — see `turnOffRefusal`. */
		turnOnRefusal: doorRefusalSchema.optional(),
	})
	.readonly()
	.refine((switchable) => switchable.deadNarratives === undefined || switchable.poweredWhen !== undefined, {
		message: 'deadNarratives requires poweredWhen — a device with no power condition is never dead, so the lines could never speak',
		path: ['deadNarratives'],
	})
	.refine((switchable) => switchable.latchedNarratives === undefined || switchable.latching === true, {
		message: 'latchedNarratives requires latching — a switch that freely reverses never refuses, so the lines could never speak',
		path: ['latchedNarratives'],
	});

/** A thing with a switch — see {@link switchableSchema}. */
export type Switchable = z.infer<typeof switchableSchema>;

/**
 * One authored item. `nouns` (at least one) plus `aliases` and `adjectives`
 * feed parser-catalog derivation; the visibility conditions gate whether the
 * item is referable in a room versus from inventory; `isPortable` gates
 * `take`. `askNarratives` (R1.1, D17 amendment: `ask` joined the slice verb
 * budget) carries EURYDICE's dossier response for `ask about <item>`; its
 * ABSENCE is authored silence — the engine falls back to its generic
 * deflection, and a deliberate non-answer stays deliberate.
 */
export const itemDefinitionSchema = z
	.strictObject({
		adjectives: z.array(nonBlankStringSchema).readonly().default([]),
		aliases: z.array(nonBlankStringSchema).readonly().default([]),
		/** D234: what this thing turns of a foe's strike while it is WORN — see `combat.ts`. Requires `equippable`: armor counts only while equipped. */
		armor: armorSchema.optional(),
		askNarratives: z.array(narrativeLineSchema).min(1).readonly().optional(),
		/**
		 * R25: how much of the carry capacity this item occupies (default 1
		 * when the pack declares a `carryCapacity`; meaningless without one).
		 * Bulk over count because a pry bar and a keycard are not one unit
		 * each — a count-limited pack is simply one where every item is bulk 1
		 * (sugar desugars; one implementation).
		 */
		bulk: positiveIntegerSchema.optional(),
		/** D135: `close X` refused in this thing's own words — see {@link doorRefusalSchema}. */
		closeRefusal: doorRefusalSchema.optional(),
		/** D50: this thing is inside another thing — or, D240, carried by a body — see `containment.ts`. */
		containedIn: containedInSchema.optional(),
		/*
		 * D55: a plain string, or conditional text. `openable`'s per-state pair
		 * below is SUGAR over this, desugared by the one describer in
		 * engine-runtime — not a second mechanism running beside it.
		 */
		description: conditionalTextSchema,
		/** D140: sentences appended to the description while their conditions hold — see {@link descriptionFragmentSchema}. */
		descriptionFragments: z.array(descriptionFragmentSchema).readonly().default([]),
		/**
		 * R25 (Calder's §1.3): what dropping this thing sounds like — exactly
		 * the `takeNarratives` pattern, so retiring the sixteen voice-covered
		 * `drop` refusals is a MIGRATION rather than a deletion: the honest
		 * half of each block moves here, the dishonest half goes in the bin.
		 * Absence = the engine's `dropAcknowledgement` slot.
		 */
		dropNarratives: z.array(narrativeLineSchema).min(1).readonly().optional(),
		/** D152: how many times this thing can be spent, by what, and what it says as it goes — see {@link durabilitySchema}. */
		durability: durabilitySchema.optional(),
		/**
		 * D143: a thing that can be WORN or WIELDED — `equip`/`wear`/`put on` —
		 * into one of the pack's declared `equipmentSlots`. Equipping requires
		 * carrying (the parser prefers the carried one, D134); one thing per
		 * slot; dropping an equipped thing takes it off first. `onEquip` and
		 * `onUnequip` are the hooks the arc's mask uses to stop the clock.
		 */
		equippable: equippableSchema.optional(),
		/*
		 * D239: what FINDING this thing says — spoken in place of the engine's one
		 * reveal sentence when a search turns it up. Authored on the found thing,
		 * like `takeNarratives`; requires `containedIn.concealed` (the loader says
		 * so: nothing else is ever found). Absent, the engine's sentence speaks.
		 */
		foundNarratives: z.array(narrativeLineSchema).min(1).readonly().optional(),
		/** D217: the group this thing belongs to, whose description answers when a plural names the set (see `groups.ts`). */
		groupId: groupIdSchema.optional(),
		inspectNarratives: z.array(narrativeLineSchema).min(1).readonly().optional(),
		inventoryVisibilityCondition: conditionSchema.optional(),
		isPortable: z.boolean(),
		itemId: itemIdSchema,
		// R4/D19 narrative-channel completions, both following the askNarratives
		// pattern (optional; ABSENCE is authored silence): `inspectNarratives`
		// speak AFTER `description` on inspect (the log's three-beat arc needs
		// EURYDICE to comment on a read); `takeNarratives` speak on a successful
		// take (absence = the engine's generic acknowledgement, and — for the
		// hidden log — her load-bearing silence).
		/** R12: the found document this item carries — see logs.ts (reading is inspecting). */
		log: itemLogSchema.optional(),
		name: nonBlankStringSchema,
		/**
		 * D182: whether the engine's lines may put "the" in front of the name.
		 * Thirty-odd built-in responses are written `the {itemName}`, which is
		 * right for a kettle and wrong for a name that is already a whole noun
		 * phrase — `yourself`, a proper noun, "your hands". `false` and the
		 * engine drops the article wherever it wrote one, so a pack can name the
		 * player's own body `yourself` and read "Nothing about yourself answers
		 * to KICK" rather than "the yourself" (Calder's finding, 2026-09-14: the
		 * POC named it `body` only because `body` survives the article).
		 */
		nameTakesArticle: z.boolean().default(true),
		nouns: z.array(nonBlankStringSchema).min(1).readonly(),
		/** D50: this thing has a door — see {@link openableSchema}. */
		openable: openableSchema.optional(),
		/**
		 * D135: `open X` refused in this thing's own words — the twin of
		 * `closeRefusal`, for the thing that is a door in the fiction but not a
		 * lid the engine works: the stair door with its handle on the other
		 * side. Without it, a lidless door would be told it "has nothing on it
		 * that opens or shuts", which is a lie about a door.
		 */
		openRefusal: doorRefusalSchema.optional(),
		/*
		 * R29 (D105; Calder's D104 §3.1): "you can see this and not reach it" —
		 * a genre primitive (behind glass, across a chasm, on a high shelf).
		 * While the condition is UNMET the item stays referable and describable
		 * — `inspect`, `ask about`, and the room's own prose are untouched,
		 * because the player can SEE it — but every acting verb refuses, one
		 * interception rather than a list of verbs, so a verb added next year
		 * is covered without anyone remembering this. Absent = always
		 * reachable. `conditionSchema` verbatim: no new vocabulary, and the
		 * constant-condition validator inherits the check for free.
		 */
		reachableWhen: conditionSchema.optional(),
		/*
		 * D98: what taking this thing back says, after the player set it down.
		 * `takeNarratives` were authored for the item's PLACEMENT and only the
		 * first take stands there — the acquisition record's write-once
		 * semantics are the narrative layer's fact too, so a re-take speaks
		 * this instead (Calder's retake-lie finding: "You free the bar from
		 * the wreck of the shelving", in a corridor with no shelving). Absent,
		 * the `retakeAcknowledgement` slot speaks — absence is silence.
		 */
		retakeNarratives: z.array(narrativeLineSchema).min(1).readonly().optional(),
		roomVisibilityCondition: conditionSchema.optional(),
		/*
		 * D50: what SEARCHING this thing says, when searching it turns up
		 * nothing. Calder's first ask, and the one that decides whether the verb
		 * is worth typing anywhere: "You find nothing" is the failure state that
		 * makes searching worth doing everywhere else, and a flat refusal teaches
		 * the player the verb is decorative. Absent, the engine's slots speak:
		 * `searchFoundOnlyInSight`, naming what the thing still holds in plain
		 * sight (D247), else `searchFoundNothing`.
		 */
		searchNarratives: z.array(narrativeLineSchema).min(1).readonly().optional(),
		/** D244: a fixed thing the player takes FROM while a counter allows — see {@link itemStockSchema}. */
		stock: itemStockSchema.optional(),
		/** D56: this thing has a switch — see {@link switchableSchema}. */
		switchable: switchableSchema.optional(),
		takeNarratives: z.array(narrativeLineSchema).min(1).readonly().optional(),
		/**
		 * D199: `take X` refused in this thing's own words — D135's shape on a
		 * third verb. The engine's `notPortable` slot is one sentence for every
		 * fixed thing in a pack, which is right for a bench and wrong for a man
		 * who will not come out of a hollow: the refusal there is the pack's
		 * best line, and it names the limit doing the refusing
		 * (content-standards §2.6). `when` makes it conditional — a thing that
		 * cannot be taken YET; absent, it always refuses. It is checked before
		 * portability, so a portable thing may carry one too.
		 */
		takeRefusal: doorRefusalSchema.optional(),
		/*
		 * D231: said when this thing is ADDRESSED about a subject no topic answers,
		 * after the subject's own `askNarratives` had their chance; absent, the
		 * `askDeflection` slot. Requires `topics` (the loader says so).
		 */
		topicFallbackNarratives: z.array(narrativeLineSchema).min(1).readonly().optional(),
		/* D231: the keyed answer table (`topics.ts`); `askNarratives` is its one-topic case and keeps working — a topic answers first. */
		topics: topicsSchema.optional(),
		/*
		 * D47: what THIS thing says when a recognized-but-unmechanized verb is
		 * aimed at it. D35 taught the engine to refuse eighteen declared verbs
		 * by name, which was courtesy; answering them in character is the
		 * product, and without this every verb/item pair — hundreds of them —
		 * shared a single sentence in a game whose thesis is that failures are
		 * specific.
		 *
		 * Keyed by CANONICAL verb token, so one entry covers every alias the
		 * vocabulary maps to it ("rap" reaches the `knock` beat). Absence is
		 * silence, as everywhere else here: no entry, and the
		 * `unsupportedIntentOnTarget` slot speaks.
		 *
		 * It re-voices a refusal and never makes the verb work — same discipline
		 * as D25's response overrides. The typed rejection and the unconsumed
		 * turn are identical either way; only the words change. Keys that name
		 * no declared verb, or name a SUPPORTED one, are rejected by
		 * `validate-content` rather than shipping as lines nothing can reach.
		 */
		/*
		 * R29: what refusing to act across the barrier says — the
		 * `dropNarratives` pattern exactly: authored lines outrank the
		 * `outOfReach` slot, absence is the slot speaking. Requires
		 * `reachableWhen` (a refusal for a barrier that never bars is dead
		 * content — the D47-family validator names it).
		 */
		unreachableNarratives: z.array(narrativeLineSchema).min(1).readonly().optional(),
		unsupportedVerbNarratives: unsupportedVerbNarrativesSchema.optional(),
		useInteractions: z.array(useInteractionSchema).readonly().default([]),
		/*
		 * D196: what a pack-declared verb DOES to this thing — the one-object
		 * general case `useInteractions` is the sugar for. Keyed by canonical
		 * token like `unsupportedVerbNarratives`, and the two are disjoint by
		 * the loader's rule: an unsupported verb can only refuse, an authored
		 * verb only acts.
		 */
		verbInteractions: verbInteractionsSchema.optional(),
		/** D234: what this thing does when it is swung at a foe — see `combat.ts`. A weapon is a thing that happens to hurt. */
		weapon: weaponSchema.optional(),
	})
	.readonly()
	// D234: armor nothing can wear is a number nothing reads — unrepresentable rather than refused at load.
	.refine((item) => item.armor === undefined || item.equippable !== undefined, {
		message: 'armor on an item with no `equippable` — armor counts only while it is worn, and nothing can wear this',
		path: ['armor'],
	})
	/*
	 * D55 ruled one description mechanism per item; D159 made it the only
	 * spelling — `openable.openDescription` / `closedDescription` retired into
	 * the `container_open` variants they always desugared to. The 0.2.0 → 0.3.0
	 * migration folds them; nothing here has to refuse a second spelling.
	 */
	/*
	 * D135: a `closeRefusal` with no `when` on a thing that HAS a door refuses
	 * every close, which makes the door one-way by accident and strands its
	 * own closing — `closedNarratives`, the shut reading, the whole
	 * mechanism — as content nothing can reach. A one-way door is a real thing
	 * to author (locker eleven), and the way to author it is to say WHEN.
	 */
	.refine((item) => item.closeRefusal === undefined || item.openable === undefined || item.closeRefusal.when !== undefined, {
		message:
			'closeRefusal on an openable item must declare `when` — an unconditional refusal makes the door one-way by accident and strands its own closing; say when the refusal applies',
		path: ['closeRefusal', 'when'],
	})
	.refine((item) => item.openRefusal === undefined || item.openable === undefined || item.openRefusal.when !== undefined, {
		message:
			'openRefusal on an openable item must declare `when` — an unconditional refusal strands the door\'s own opening; say when the refusal applies',
		path: ['openRefusal', 'when'],
	})
	// D152: a thing spent by wearing must be wearable, or the counter can never move.
	.refine(
		(item) =>
			item.durability === undefined ||
			item.equippable !== undefined ||
			![...item.durability.spentBy, ...refusedActsOf(item.durability)].some((act) => act === 'equip' || act === 'unequip'),
		{
			message: 'durability names `equip` or `unequip` on an item with no `equippable` — nothing can wear it, so nothing can spend it that way',
			path: ['durability', 'spentBy'],
		},
	)
	// D244: a stock is taken FROM, never lifted — a portable one would answer `take` twice.
	.refine((item) => item.stock === undefined || !item.isPortable, {
		message: 'a stock is a fixed thing the player takes from — a portable thing with a stock would answer `take` two ways; drop isPortable',
		path: ['stock'],
	});

/** One authored item — see {@link itemDefinitionSchema}. */
export type ItemDefinition = z.infer<typeof itemDefinitionSchema>;
